import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { comprimirImagen } from "@/lib/image";
import { INSUMO_ESTADO_COLOR } from "@/lib/constants";
import { INSUMO_ESTADO_EN, fechaHoraEn } from "@/lib/chofer";
import { cn } from "@/lib/utils";
import { PillDot } from "@/components/marca/Pills";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, PackagePlus, Receipt, Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

export default function ChoferInsumos() {
  const navigate = useNavigate();
  const [categorias, setCategorias] = useState([]);
  const [items, setItems] = useState([]);
  const [modo, setModo] = useState(null); // "pedido" | "gasto"
  const [comprobanteFor, setComprobanteFor] = useState(null);

  const cargar = useCallback(async () => {
    try { const { data } = await api.get("/chofer/insumos"); setItems(data); } catch { /* */ }
  }, []);

  useEffect(() => {
    api.get("/categorias-gasto?solo_activos=true").then((r) => setCategorias(r.data)).catch(() => {});
    cargar();
  }, [cargar]);

  return (
    <div className="bg-paper">  {/* Fix Q (2026-10-13): app del chofer en inglés; el alto y el margen inferior los pone ChoferLayout */}
      <header className="bg-paper sticky top-0 z-10 border-b border-line">
        <div className="max-w-md mx-auto flex items-center gap-1 px-2 py-2">
          <button data-testid="insumos-volver" onClick={() => navigate("/chofer")} className="h-12 w-12 flex items-center justify-center text-ink" aria-label={t("Back")}><ArrowLeft className="h-6 w-6" /></button>
          <h1 className="font-display font-bold text-[26px] leading-[1.05] text-ink">{t("Supplies")}</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <button data-testid="insumo-necesito-btn" onClick={() => setModo("pedido")} className="h-28 rounded-xl border border-line-strong bg-white flex flex-col items-center justify-center gap-2">
            <PackagePlus className="h-7 w-7 text-ink" />
            <span className="font-display font-semibold text-[17px] text-ink">{t("I need something")}</span>
          </button>
          <button data-testid="insumo-compre-btn" onClick={() => setModo("gasto")} className="h-28 rounded-xl border border-line-strong bg-white flex flex-col items-center justify-center gap-2">
            <Receipt className="h-7 w-7 text-ink" />
            <span className="font-display font-semibold text-[17px] text-ink">{t("I bought something")}</span>
          </button>
        </div>

        <div className="space-y-2">
          <p className="eyebrow">{t("My requests and expenses")}</p>
          {items.length === 0 && <p className="text-sm text-ink-3 text-center py-6">{t("You have no requests or expenses yet")}</p>}
          {items.map((i) => (
            <div key={i.id} data-testid={`insumo-item-${i.id}`} className="bg-white rounded-xl border p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-ink">{i.categoria_nombre}</p>
                <PillDot color={INSUMO_ESTADO_COLOR[i.estado]}>{INSUMO_ESTADO_EN[i.estado]}</PillDot>
              </div>
              <p className="text-sm text-ink-2 mt-1">{[i.tipo === "pedido" ? i.cantidad : i.descripcion, i.monto != null ? `$${i.monto.toFixed(2)}` : ""].filter(Boolean).join(" · ")}</p>
              <p className="text-xs text-ink-3 mt-1">{fechaHoraEn(i.created_at)}</p>
              {i.estado === "rechazado" && i.nota && <p className="text-xs text-danger mt-1">{t("Reason: {nota}", { nota: i.nota })}</p>}
              {i.tipo === "gasto" && i.estado === "aprobado" && (
                <Button data-testid={`insumo-subir-comprobante-${i.id}`} size="sm" className="mt-2 h-9 bg-brand hover:bg-brand-dark text-white" onClick={() => setComprobanteFor(i)}>
                  <Camera className="h-4 w-4 mr-1" />{t(" Upload receipt")}</Button>
              )}
            </div>
          ))}
        </div>
      </main>

      {modo === "pedido" && <PedidoDialog categorias={categorias} onClose={() => setModo(null)} onDone={() => { setModo(null); cargar(); }} />}
      {modo === "gasto" && <GastoDialog categorias={categorias} onClose={() => setModo(null)} onDone={() => { setModo(null); cargar(); }} />}
      {comprobanteFor && <ComprobanteDialog insumo={comprobanteFor} onClose={() => setComprobanteFor(null)} onDone={() => { setComprobanteFor(null); cargar(); }} />}
    </div>
  );
}

function PedidoDialog({ categorias, onClose, onDone }) {
  const [categoriaId, setCategoriaId] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    if (!categoriaId) return toast.error(t("Choose a category"));
    if (!cantidad.trim()) return toast.error(t("Enter the quantity"));
    setSaving(true);
    try {
      await api.post("/chofer/insumos/pedido", { categoria_id: categoriaId, cantidad, nota });
      toast.success(t("Request sent"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>{t("I need something")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="eyebrow">{t("Category")}</Label>
            <Select value={categoriaId} onValueChange={setCategoriaId}>
              <SelectTrigger data-testid="pedido-categoria" className="h-12 bg-white mt-1"><SelectValue placeholder={t("Choose a category")} /></SelectTrigger>
              <SelectContent className="bg-white">
                {categorias.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="eyebrow">{t("Quantity")}</Label><Input data-testid="pedido-cantidad" value={cantidad} onChange={(e) => setCantidad(e.target.value)} placeholder={t("e.g. 2 gallons")} className="mt-1 h-12" /></div>
          <Textarea data-testid="pedido-nota" placeholder={t("Note (optional)")} value={nota} onChange={(e) => setNota(e.target.value)} rows={2} />
          <div className="flex gap-2">
            <Button variant="outline" className="h-12 flex-1" onClick={onClose}>{t("Cancel")}</Button>
            <Button data-testid="pedido-enviar" className="h-12 flex-1 bg-brand hover:bg-brand-dark text-white" onClick={guardar} disabled={saving}>{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("Send")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GastoDialog({ categorias, onClose, onDone }) {
  const [categoriaId, setCategoriaId] = useState("");
  const [monto, setMonto] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [comprobante, setComprobante] = useState(null);
  const [saving, setSaving] = useState(false);

  const cat = categorias.find((c) => c.id === categoriaId);
  const habitual = cat?.habitual;

  const onFoto = async (e) => {
    const f = e.target.files?.[0];
    if (f) setComprobante(await comprimirImagen(f));
    e.target.value = "";
  };

  const guardar = async () => {
    if (!categoriaId) return toast.error(t("Choose a category"));
    if (!monto) return toast.error(t("Enter the amount"));
    if (habitual && !comprobante) return toast.error(t("The receipt photo is required"));
    if (!habitual && !descripcion.trim()) return toast.error(t("Enter a description"));
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("categoria_id", categoriaId);
      fd.append("monto", monto);
      fd.append("descripcion", descripcion);
      if (comprobante) fd.append("comprobante", comprobante);
      await api.post("/chofer/insumos/gasto", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(habitual ? t("Expense recorded") : t("Expense sent for approval"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>{t("I bought something")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="eyebrow">{t("Category")}</Label>
            <Select value={categoriaId} onValueChange={setCategoriaId}>
              <SelectTrigger data-testid="gasto-categoria" className="h-12 bg-white mt-1"><SelectValue placeholder={t("Choose a category")} /></SelectTrigger>
              <SelectContent className="bg-white">
                {categorias.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {categoriaId && !habitual && (
            <p data-testid="gasto-requiere-aprobacion" className="text-xs text-[#7A5A10] bg-[#FBF5E6] border border-[#E9D9B0] rounded-lg p-2">{t("This category needs approval before the expense is recorded.")}</p>
          )}
          <div><Label className="eyebrow">{habitual ? t("Amount") : t("Estimated amount")}</Label><Input data-testid="gasto-monto" type="number" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} className="mt-1 h-12" /></div>
          {categoriaId && !habitual && (
            <Textarea data-testid="gasto-descripcion" placeholder={t("Description")} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} />
          )}
          {categoriaId && habitual && (
            comprobante ? (
              <div className="flex items-center justify-between bg-surface-2 rounded-lg px-3 py-2 text-sm">
                <span className="truncate">{comprobante.name}</span>
                <button onClick={() => setComprobante(null)}><X className="h-4 w-4 text-ink-3" /></button>
              </div>
            ) : (
              <label data-testid="gasto-foto-btn" className="flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-dashed border-line-strong text-ink-3 cursor-pointer">
                <Camera className="h-5 w-5" />{t(" Receipt photo *")}<input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFoto} />
              </label>
            )
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="h-12 flex-1" onClick={onClose}>{t("Cancel")}</Button>
            <Button data-testid="gasto-enviar" className="h-12 flex-1 " onClick={guardar} disabled={saving}>{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("Send")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ComprobanteDialog({ insumo, onClose, onDone }) {
  const [monto, setMonto] = useState(insumo.monto != null ? String(insumo.monto) : "");
  const [comprobante, setComprobante] = useState(null);
  const [saving, setSaving] = useState(false);

  const onFoto = async (e) => {
    const f = e.target.files?.[0];
    if (f) setComprobante(await comprimirImagen(f));
    e.target.value = "";
  };

  const guardar = async () => {
    if (!monto) return toast.error(t("Enter the final amount"));
    if (!comprobante) return toast.error(t("The receipt photo is required"));
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("monto", monto);
      fd.append("comprobante", comprobante);
      await api.post(`/chofer/insumos/${insumo.id}/comprobante`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Receipt sent"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>{t("Upload receipt — {categoria_nombre}", { categoria_nombre: insumo.categoria_nombre })}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="eyebrow">{t("Final amount")}</Label><Input data-testid="comprobante-monto" type="number" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} className="mt-1 h-12" /></div>
          {comprobante ? (
            <div className="flex items-center justify-between bg-surface-2 rounded-lg px-3 py-2 text-sm">
              <span className="truncate">{comprobante.name}</span>
              <button onClick={() => setComprobante(null)}><X className="h-4 w-4 text-ink-3" /></button>
            </div>
          ) : (
            <label data-testid="comprobante-foto-btn" className="flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-dashed border-line-strong text-ink-3 cursor-pointer">
              <Camera className="h-5 w-5" />{t(" Receipt photo *")}<input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFoto} />
            </label>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="h-12 flex-1" onClick={onClose}>{t("Cancel")}</Button>
            <Button data-testid="comprobante-enviar" className="h-12 flex-1 bg-brand hover:bg-brand-dark text-white" onClick={guardar} disabled={saving}>{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("Record")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
